<template>
    <AdminLayout>
        <section class="flex items-start px-14 space-x-9">
            <div class="w-full">

                <div v-if="showRoleForm" class="mt-12">
                    <form @submit.prevent="addNewRole">
                        <div class="border rounded-lg py-7 px-8">
                            <div class="flex justify-between">
                                <p class="font-semibold text-xl text-neutral-800">Role Allocation Settings</p>
                            </div>
                            <div class="addNewSubss">
                                <label>Role Name:</label>
                                <input class="addNewInput" type="text" placeholder="Role Name" v-model="roleName" />
                                <p class="text-red-600" v-if="titleMissing">This field is required</p>
                            </div>
                            
                            <div class="my-6">
                                <multiselect
                                    v-model="selectedPermissions"
                                    :options="permissionsList"
                                    :multiple="true"
                                    :close-on-select="false"
                                    :clear-on-select="false"
                                    :preserve-search="true"
                                    placeholder="Select one or more permissions"
                                    label="value"
                                    track-by="value"
                                    class="w-full"
                                />
                                <p class="text-red-600" v-if="roleMissing">This field is required</p>
                            </div>
                            
                            <div class="pt-4 text-right">
                                <button @click="showRoleForm = false" type="button" class="ml-auto mr-2 btn-gray-light">
                                    <span class="pr-2"> Cancel </span>
                                </button>
                                <button type="submit" class="btn-primary-light flex items-center" :disabled="buttonclick" :class="buttonclick == true ? 'opacity-50 cursor-not-allowed' : ''">
                                    <span class="pr-2"> Submit </span>
                                    <span>
                                        <span v-if="this.btnLoader" class="animate-spin h-4 w-4 inline-block border-2 border-white border-r-white/10 border-t-white/10 rounded-full"></span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="mt-12">
                    <div class="border rounded-lg py-7 px-8">
                        <div class="flex justify-between mb-5">
                            <p class="font-semibold text-xl text-neutral-800">Listed Roles</p>
                            <div class="flex items-center">
                                <button v-if="!showRoleForm" @click="showRoleForm = true" class="ml-3 btn-primary-light flex items-center">
                                    <span class="pl-2"> Add New Role </span>
                                </button>
                            </div>
                        </div>
                        <div v-if="isLoading">
                            <div class="grid grid-cols-4 bg-[#ECF6FF] divide-x-[1px] animate-pulse">
                                <div class="px-6 py-4">
                                    <span class="w-full rounded h-7 bg-neutral-200 block"></span>
                                </div>
                                <div class="px-6 py-4 col-span-2">
                                    <span class="w-full rounded h-7 bg-neutral-200 block"></span>
                                </div>
                                <div class="px-6 py-4">
                                    <span class="w-full rounded h-7 bg-neutral-200 block"></span>
                                </div>
                            </div>
                            <div class="grid grid-cols-4 bg-white divide-x-[1px] shadow mt-1.5 animate-pulse" v-for="index in 8" :key="index">
                                <div class="px-6 py-4">
                                    <span class="w-full rounded h-7 bg-neutral-200 block"></span>
                                </div>
                                <div class="px-6 py-4 col-span-2">
                                    <span class="w-full rounded h-7 bg-neutral-200 block"></span>
                                </div>
                                <div class="px-6 py-4">
                                    <span class="w-full rounded h-7 bg-neutral-200 block"></span>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div class="grid grid-cols-4 text-xs text-neutral-400 uppercase bg-[#ECF6FF] divide-x divide-[#eaeaea]">
                                <div class="px-6 py-3 font-semibold text-[#404A5A]">Role Name</div>
                                <div class="px-6 py-3 font-semibold text-[#404A5A] col-span-2">Permission Modules</div>
                                <div class="px-6 py-3 font-semibold text-[#404A5A]">Action</div>
                            </div>
                            <div v-for="(row, index) in rows" :key="index" class="grid grid-cols-4 divide-x divide-y divide-[#eaeaea]">
                                <!-- Role Name Field -->
                                <div class="px-6 py-4 border-t border-[#eaeaea]">
                                    <div v-if="!row.editing">{{ row.role }}</div>
                                    <input v-else v-model="row.role" type="text" class="border p-1 w-full" />
                                </div>

                                <div class="px-6 py-4 col-span-2">
                                    <div v-if="!row.editing" class="flex flex-wrap gap-1">
                                        <span class="inline-flex rounded-2xl px-3 py-1 bg-blue-400 text-white text-xs" v-for="(permission, index) in row.permissions" :key="index">{{permission.value}}</span>
                                    </div>
                                    <Multiselect 
                                        v-else 
                                        v-model="row.permissions" 
                                        :options="permissionsList" 
                                        :multiple="true" 
                                        :close-on-select="false" 
                                        :clear-on-select="false" 
                                        :preserve-search="true" 
                                        placeholder="Select multiple permissions" 
                                        label="value" 
                                        track-by="value" 
                                        class="w-full"
                                    />
                                </div>

                                <!-- Action Buttons -->
                                <div v-if="row.role !== 'Super Admin' && row.role !== 'General User'"> 
                                    <div class="px-6 py-4 flex gap-2">
                                        <!-- Edit Button (Show when not editing) -->
                                        <button v-if="!row.editing" @click="editRow(row)" class="border mr-2 rounded-3xl px-3 py-1.5 text-xs inline-block border-orange-200 bg-orange-50 text-orange-600">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"></path></svg>
                                        </button>

                                        <!-- Save & Cancel Buttons (Show when editing) -->
                                        <template v-if="row.editing">
                                            <button @click="saveRow(row)" class="border mr-2 rounded-3xl px-3 py-1.5 text-xs inline-block border-orange-200 bg-orange-50 text-orange-600" type="button">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"></path></svg>
                                            </button>
                                            
                                            <button @click="cancelEdit(row)" class="border mr-2 rounded-3xl px-3 py-1.5 text-xs inline-block border-orange-200 bg-orange-50 text-orange-600">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
                                            </button>
                                        </template>

                                        <!-- Delete Button (Always visible) -->
                                        <!-- <button v-if="!row.editing" @click="deleteRow(index)" class="border mr-2 rounded-3xl px-3 py-1.5 text-xs inline-block border-orange-200 bg-orange-50 text-orange-600">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"></path></svg>
                                        </button> -->
                                    </div>
                                </div>  

                                <div v-else class="px-6 py-4">
                                    This Role Cannot be Edited.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- <RightSideBar /> -->
        </section>
    </AdminLayout>
</template>
<script>
import AdminLayout from '../AdminDashboard/AdminLayout.vue';
// import RightSideBar from '../../components/RightSideBar.vue';
import { createToast } from 'mosha-vue-toastify';
import axios from "axios";
import { BASE_URL } from "../../config";
import { createLocal } from 'the-storages';
import "vue-multiselect/dist/vue-multiselect.css";
import Multiselect from 'vue-multiselect'
const mirror = createLocal();
const storage = mirror._prx;


export default {
    name: 'CountryList',
    components: {
        AdminLayout,
        // RightSideBar,
        Multiselect 
    },
    data() {
        return {
            buttonclick: false,
            btnLoader: false,
            storage: storage,           
            permissionsList:[],
            selectedPermissions: [],
            rows: [],
            showRoleForm: false,
            roleName: "",
            isOpen: false,
            selected: [],  // Holds selected values
            selectedTwo: [],  // Holds selected values
            options: ['add-employee', 'payroll-settings', 'employee-list', 'organization-settings'],
            optionsTwo: ['add-employee', 'payroll-settings', 'employee-list'],
            titleMissing: false,
            roleMissing: false,
            isLoading: false,
        }
    },
    created() {
        this.getRoleList();
        this.getPermissionList();
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
      
        async getPermissionList() {
        this.isLoading = true; // ✅ Start loader

        try {
            const formData = {
                company_id: this.storage.companyDetails.id
            };
            const response = await axios.get(BASE_URL + '/permissions', { params: formData }); // ✅ GET with params
            this.permissionsList = response.data.permissionsList.map(permission => ({
                value: permission.name,
                // label: permission.name
            }));
            console.log("PERMISSIONS LIST:: ", response.data);
        } catch (error) {
            console.error("Error fetching permissions list:", error);
        } finally {
            this.isLoading = false; // ✅ Stop loader
        }
    },

        async getRoleList() {
            const formData = {
                "company_id": this.storage.companyDetails.id
            };
            try {
                const response = await axios.post(BASE_URL + '/get-roles', formData);
                if (response.data && response.data.roleList) {
                    this.rows = response.data.roleList.map(role => ({
                        id: role.id,
                        role: role.name,
                        editing: false,
                        permissions: Array.isArray(role.permissions)
                            ? role.permissions.map(permission => ({
                                value: permission
                            }))
                            : [] // Default to an empty array if permissions is null or not an array
                    }));
                } else {
                    console.error("Role list is empty or invalid:", response.data);
                    this.rows = []; // Default to an empty array if roleList is missing
                }
            } catch (error) {
                console.error("Error fetching role list:", error);
                this.rows = []; // Default to an empty array in case of an error
            }
        },

        async addNewRole() {
            if(this.roleName == '') {
                this.titleMissing = true;
                return false;
            } else {
                this.titleMissing = false;
            }

            if(this.selectedPermissions.length == 0) {
                this.roleMissing = true;
                return false;
            } else {
                this.roleMissing = false;
            }

            const formData = {
                "company_id": this.storage.companyDetails.id,
                "name": this.roleName,
                "permissions": this.selectedPermissions.map(permission => permission.value)
            };
            console.log("FORM DATA: ", formData);

            this.btnLoader = true;
            this.buttonclick = true;
            await axios.post(BASE_URL + '/roles', formData)
            .then((response) => {
                this.roleName = "";
                this.selectedPermissions = [];
                this.showRoleForm = false;
                this.getRoleList();
                if(response.data.success === 'success'){
                    this.btnLoader = false; 
                    this.buttonclick = false;
                    this.tostMessage('success', response.data.message);
                }else{
                    this.btnLoader = false;
                    this.buttonclick = false;
                    this.tostMessage('danger', response.data.message);
                }
            })
        },

        tostMessage(status,message){
            createToast({
                // title: title,
                description: message
            },
            {
                type: status,
                timeout: 2500,
                position: 'bottom-right',
                showIcon: 'true'
            })
        },
       
        clickToEdit(reportingManager) {
            //this.editDiv[index] = true;
            reportingManager.editDiv = !reportingManager.editDiv; // Toggle the editable stat
        },
        editRow(row) {
            row.editing = true;
        },
        async saveRow(row) {
            row.editing = false;
            console.log("Updated Row: ", row);

            const formData = {
                "role_id": row.id,
                "name": row.role,
                "permissions": row.permissions.map(permission => permission.value)
            };

            await axios.put(BASE_URL + '/update-roles', formData)
                .then((response) => {
                    this.getRoleList();
                    if(response.data.success === 'success'){
                        this.tostMessage('success', response.data.message);
                    }else{
                        this.tostMessage('danger', response.data.message);
                    }
                    console.log("REPORTING MANAGER LIST:: ", response.data);
                })
        },
     
        cancelEdit(row) {
            // Revert to original data
            Object.assign(row, row.originalData);
            row.editing = false;
        },

        handleClickOutside(event) {
            if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
                this.isOpen = false;
            }
        },       
    }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style>
/* Dropdown Container */
.dropdown-container {
    position: relative;
    display: block;
    width: 100%;
}

/* Input Box */
.dropdown-input {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    background: white;
    font-size: 15px;
    line-height: normal;
}

.placeholder {
    color: #6b7280;
}

.selected-count {
    font-weight: 500;
}

.dropdown-icon {
    color: #6b7280;
}

.dropdown-menu {
    position: absolute;
    left: 0;
    background: white;
    border-radius: 6px;
    box-shadow: 0px 1px 8px #3e3b3b33;
    margin-top: 0;
    width: 100%;
    z-index: 1000;
    top: 45px;
}

.dropdown-content {
    padding: 10px;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    cursor: pointer;
    transition: background 0.3s;
    font-size: 14px;
    color: #000000;
}

.dropdown-item:hover {
    background: #f1f1f1;
}

/* Selected Tags */
.selected-tags {
    margin-top: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.tag {
    background: #111827;
    color: white;
    padding: 4px 10px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
}

.remove-icon {
    cursor: pointer;
    font-size: 12px;
}

.remove-icon:hover {
    color: #ff4d4d;
}

.addNewSubss label {
    font-size: 15px;
    font-weight: 500;
    color: #3c3c3c;
    margin-bottom: 3px;
    display: block;
}

.addNewSubss input {
    border: none;
    background-color: #f5f5f5;
    padding: 12px 15px;
    display: block;
    width: 100%;
    font-size: 13px;
    color: #666;
    resize: none;
    font-weight: 500;
    border-radius: 4px;
}

.update.bg-white.border-b {
    /* border: 1px solid black; */
    border-radius: 123px !important;
    background: #eff6ff;
}

.green {
    color: aquamarine;
}

.red {
    color: rgb(253, 164, 164);
}

.addNewBox1 .addNewSub .adnright,
.addNewBox1 {
    padding: 0px;
    margin: 0px;
}

.addNewBox1 .addNewSub .adnright,
.addNewBox1 .addNewSub .adnleft {
    flex: 1 0 50%;
}

.addNewBox1 .addNewSub textarea,
.addNewBox1 .addNewSub select,
.addNewBox1 .addNewSub input {
    border: none;
    background-color: #f5f5f5;
    padding: 12px 15px;
    display: block;
    width: 100%;
    font-size: 13px;
    color: #666;
    resize: none;
    font-weight: 500;
    border-radius: 4px;
}

.addNewBox1 .addNewSub label {
    font-size: 15px;
    font-weight: 500;
    color: #3c3c3c;
    margin-bottom: 2px;
}
select:focus{
    outline: none;
}
</style>